import React from "react";
import {
  Box,
  Button,
  Link,
  MagnifyingGlassIcon,
  TextInput,
  Typography
} from "@dealer-center/core-components";
import {
  CARD_STYLES,
  CONTAINER_STYLES,
  LINK_STYLES,
  TYPOGRAPHY_STYLES
} from "../styleConstants";
import { CardIcon } from "./CardIcon";

interface ApplicationCardProps {
  title: string;
  description: string;
  icon: string;
  iconColor: string;
  url?: string;
  isExternal?: boolean;
  onClick?: () => void;
  hasSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  children?: React.ReactNode;
}

export const ApplicationCard: React.FC<ApplicationCardProps> = ({
  title,
  description,
  icon,
  iconColor,
  url,
  isExternal,
  onClick,
  hasSearch,
  searchValue,
  onSearchChange,
  children
}) => {
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue?.trim()) {
      const searchObj = {
        keywords: [searchValue.trim()]
      };

      const json = JSON.stringify(searchObj);
      const encodedSearch = encodeURIComponent(json);

      const searchUrl = `${url}&search=${encodedSearch}`;

      window.open(searchUrl, "_blank");
    } else {
      window.open(url, "_self");
    }
  };
  const cardContent = (
    <>
      {/* Card Header */}
      <Box
        className="application-header"
        sx={{
          ...CONTAINER_STYLES.flexRowCenter,
          // 4px here + 8px under the description = the old 12px, height unchanged
          marginBottom: "4px"
        }}
      >
        <CardIcon iconColor={iconColor} iconKey={icon} title={title} />
        <Typography sx={TYPOGRAPHY_STYLES.cardHeader} variant="h6">
          {title}
        </Typography>
      </Box>

      {/* Card Description */}
      <Typography
        className="application-description"
        // owns the gap to whatever follows — search form or children
        sx={{ ...TYPOGRAPHY_STYLES.cardDescription, marginBottom: "8px" }}
        variant="body-md"
      >
        {description}
      </Typography>

      {/* Inventory Search Form */}
      {hasSearch && (
        <Box
          className="inventory-search-block"
          component="form"
          sx={{
            ...CONTAINER_STYLES.flexRowCenter,
            gap: 1,
            // cancels the TextInput wrapper's reserved label space
            marginTop: "-8px"
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          onSubmit={handleSearchSubmit}
        >
          <Box
            sx={{ flexGrow: 1 }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <TextInput
              name="inventorySearch"
              placeholder="Search inventory"
              sx={{ width: "100%" }}
              value={searchValue || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onSearchChange?.(e.target.value)
              }
            />
          </Box>
          <Button
            analyticsConfiguration={{
              eventName: "welcome_search_inventory"
            }}
            sx={{
              minWidth: "auto",
              padding: "10px",
              backgroundColor: "#1B5A8A",
              marginTop: "10px",
              "&:hover": {
                backgroundColor: "#0F3D5C"
              }
            }}
            type="submit"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          >
            <MagnifyingGlassIcon
              style={{
                fontSize: "20px"
              }}
              title="Search inventory"
            />
          </Button>
        </Box>
      )}

      {/* Optional children (for additional custom content) */}
      {children}
    </>
  );

  // If children/onClick exists (like inventory search or a card-body action), render as clickable Box
  if (hasSearch || children || onClick) {
    return (
      <Box
        className="application-card inventory-card"
        sx={{
          ...CARD_STYLES,
          position: "relative",
          ...(url
            ? {
                // Scoped to the hidden card-body anchor, a DIRECT child. Sub-links
                // sit deeper and keep their own ring, so focus stays unambiguous.
                "&:has(> a:focus)": {
                  outline: "2px solid #1B5A8A",
                  outlineOffset: "2px"
                }
              }
            : {
                cursor: "default",
                // an inert body must not lift on hover — it isn't clickable
                "&:hover": { boxShadow: CARD_STYLES.boxShadow }
              })
        }}
        title={url ? `${title}: ${description}` : undefined}
        onClick={onClick}
      >
        {/* Hidden link for accessibility — only when the card body has a destination */}
        {url && (
          <Link
            aria-label={`${title}: ${description}`}
            href={url}
            sx={{
              position: "absolute",
              // px strings on purpose: MUI maps numeric width/height <= 1 to 100%
              width: "1px",
              height: "1px",
              padding: 0,
              margin: "-1px",
              overflow: "hidden",
              clipPath: "inset(50%)",
              whiteSpace: "nowrap",
              border: 0
            }}
          />
        )}
        {cardContent}
      </Box>
    );
  }

  // Regular card wrapped in Link
  return (
    <Link
      aria-label={`${title}: ${description}`}
      href={url as string}
      rel={isExternal ? "noopener noreferrer" : undefined}
      sx={{ ...LINK_STYLES.cardWrapper, width: { xs: "100%", sm: "auto" } }}
      target={isExternal ? "_blank" : undefined}
    >
      <Box className="application-card" sx={CARD_STYLES}>
        {cardContent}
      </Box>
    </Link>
  );
};
